import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { IRepresentativeSingleProfileMedia } from 'shared/interfaces/IRepresentativesEditProfile';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { profileService } from 'shared/services/profileService';
import { useDebouncedCallback } from 'use-debounce';
import { useEditProfileMedia } from '../../context/EditProfileMediaProvider';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import { useRepresentativesModal } from 'pages/Representatives/context/RepresentativesModalProvider';

const { updateProfileMediaSort } = profileService();

type EditProfileMediaDnDProps = {
  data: IRepresentativeSingleProfileMedia[];
  profileId: string;
  renderGroup: (data: IRepresentativeSingleProfileMedia[]) => ReactNode;
  renderDragOverlay: (data: IRepresentativeSingleProfileMedia, activeId: number) => ReactNode;
  agencyPermissionValue: AgencyPermissionEnums;
};

const EditProfileMediaDnD = ({
  data,
  profileId,
  agencyPermissionValue,
  renderGroup,
  renderDragOverlay,
}: EditProfileMediaDnDProps) => {
  // This state and useEffect is for DnD related
  const [attachedItem, setAttachedItem] = useState<IRepresentativeSingleProfileMedia[]>([]);

  // Error Modal
  const { handleSetMessage, handleOpen } = useRepresentativesModal();

  const prevData = useRef<IRepresentativeSingleProfileMedia[]>([]);

  // Agency Permission
  const { handleAction } = useEditProfileMedia();

  const [activeId, setActiveId] = useState<number | null>(null);
  const { mutate: updateSortMutate } = updateProfileMediaSort();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const DebounceSort = useDebouncedCallback(() => {
    const updatedMediaSort = attachedItem?.map((media, index) => ({
      id: media.id,
      sort: index,
    }));
    return updateSortMutate(
      { profileId, sort: updatedMediaSort },
      {
        onError: () => {
          setAttachedItem(prevData.current);
          handleSetMessage("Couldn't process your request", 'Your previous sorting has been restored', 'Close');
          handleOpen();
        },
        onSettled: () => {
          prevData.current = [];
        },
      },
    );
  }, 700);

  const handleOnDragStart = (event: DragEndEvent) => {
    DebounceSort.cancel();
    handleAction(agencyPermissionValue, () => {
      const { active } = event;
      if (!active) return;

      setActiveId(attachedItem?.findIndex((data) => data.id === active.id));
    });
  };

  const handleOnDragEnd = (event: DragEndEvent) => {
    handleAction(agencyPermissionValue, () => {
      setActiveId(null);
      const { active, over } = event;
      if (!active || !over) return;
      if (active.id !== over.id) {
        const oldAttachedItem = [...attachedItem];
        prevData.current = oldAttachedItem;
        setAttachedItem((prev) => {
          const oldIndex = prev.findIndex((i) => active.id === i.id);
          const newIndex = prev.findIndex((i) => over.id === i.id);
          const latest = arrayMove(prev, oldIndex, newIndex);
          return latest;
        });
        DebounceSort();
      }
    });
  };

  useEffect(() => {
    if (data) {
      setAttachedItem(data);
    }
  }, [data]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleOnDragStart}
      onDragEnd={handleOnDragEnd}
    >
      <SortableContext items={attachedItem}>{renderGroup(attachedItem)}</SortableContext>
      <DragOverlay>
        {activeId !== null && attachedItem[activeId] ? renderDragOverlay(attachedItem[activeId], activeId) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default memo(EditProfileMediaDnD);
